import { useEffect, useRef, useState } from 'preact/hooks';
import {
  CLAPPY_SQUARE, CLAPPY_VIEWBOX, CLAPPY_BOARD, CLAPPY_STICK_FACE, CLAPPY_STRIPES,
  CLAPPY_EYE_LEFT, CLAPPY_EYE_RIGHT, CLAPPY_RING,
} from '@vowric/ui';
import type { KnotProps } from './Knot';
import './CtwBot.css';

// CLAPPY — CallToWrap's mascot (named by Huan, 2026-08-24) — as the capture
// glyph. Huan's direction the same day:
// the clapperbot replaces Vowric imagery in this product, TRIALLED as the
// Knot's stand-in ("let's try"). It honours Knot's exact contract (state,
// amp, size) so every call site swaps through CaptureGlyph untouched, and
// the Knot itself remains the fleet's capture identity everywhere else.
//
// The states, translated into the mascot's own body:
//   idle          — a slow bob, and he blinks every few seconds.
//   listening     — the capture ring pulses GOLD (the fleet's reserved
//                    capture colour — kept, deliberately, so live capture
//                    still says gold even here) and the eyes widen with the
//                    voice amplitude.
//   understanding — a slow, attentive nod.
//   proposing     — he CLAPS: the stick swings at its hinge. The clapper's
//                    own word for "speaking".
//   confirmed     — one clap shut, and the ring flashes the Knot's green.
//   disabled      — dimmed, still.
//
// Reduced motion: every loop is off; states read from the static ring/dim.
// The eyes are cut as true holes (an SVG mask), so he sits on any surface.
//
// His SHAPE is not described here — it comes from @vowric/ui's clappy.ts, the
// one canonical definition the site marks and the PWA icons also render from.
// This component only says how the parts MOVE.

export function CtwBot({ state = 'idle', amp, size = 40 }: KnotProps) {
  const rootRef = useRef<HTMLSpanElement | null>(null);
  // One mask id per mounted bot — ids are document-global in SVG.
  const [maskId] = useState(() => `ctwbot-m${Math.random().toString(36).slice(2, 8)}`);

  // amp drives the eye widen through a CSS var, without re-rendering the tree
  // per frame (the mic feeds this continuously while listening).
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    el.style.setProperty('--amp', String(amp === undefined ? 0 : Math.max(0, Math.min(1, amp))));
  }, [amp]);

  return (
    <span
      ref={rootRef}
      class="ctwbot"
      data-state={state}
      style={{ width: `${size}px`, height: `${size}px` }}
      aria-hidden="true"
    >
      <svg viewBox={CLAPPY_VIEWBOX} width={size} height={size}>
        {/* the capture ring — gold while listening, green on confirmed */}
        <circle
          class="ctwbot__ring"
          cx={CLAPPY_RING.cx}
          cy={CLAPPY_RING.cy}
          r={CLAPPY_RING.r}
          fill="none"
          stroke-width={CLAPPY_RING.strokeWidth}
        />
        <g class="ctwbot__body">
          <mask id={maskId}>
            <rect x="0" y="0" width={CLAPPY_SQUARE} height={CLAPPY_SQUARE} fill="#fff" />
            <path class="ctwbot__eye ctwbot__eye--l" d={CLAPPY_EYE_LEFT} fill="#000" />
            <path class="ctwbot__eye ctwbot__eye--r" d={CLAPPY_EYE_RIGHT} fill="#000" />
          </mask>
          {/* board first, stick ON TOP — the site's mark stacks it this way
              (Huan, 2026-08-24: "I prefer the website version with it in
              front"), and the clap reads better swinging over the board. */}
          <rect
            x={CLAPPY_BOARD.x}
            y={CLAPPY_BOARD.y}
            width={CLAPPY_BOARD.width}
            height={CLAPPY_BOARD.height}
            rx={CLAPPY_BOARD.rx}
            class="ctwbot__board"
            mask={`url(#${maskId})`}
          />
          {/* the stick claps at its hinge (left end) while proposing */}
          <g class="ctwbot__stick">
            <path d={CLAPPY_STICK_FACE} class="ctwbot__stick-face" />
            <path class="ctwbot__stripes" d={CLAPPY_STRIPES} />
          </g>
        </g>
      </svg>
    </span>
  );
}
